import React, { useContext, useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BsBoxArrowRight, BsBoxSeam, BsJournalBookmark, BsList, BsPeople, BsPersonCircle, BsPlayBtn, BsSpeedometer2, BsXLg } from 'react-icons/bs';
import { MdGTranslate } from 'react-icons/md';
import { DContext } from '../../context/DataContext';
import Logo from '../../assets/Logo.svg';

const AdminLayout = () => {
  const { t, i18n } = useTranslation();
  const { currentUser, handleLogout } = useContext(DContext);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const currentLanguage = ['en', 'fr', 'ts'].includes(i18n.resolvedLanguage) ? i18n.resolvedLanguage : 'en';

  const languageOptions = [
    { value: 'en', label: t('adminLayout.languages.english') },
    { value: 'fr', label: t('adminLayout.languages.french') },
    { value: 'ts', label: t('adminLayout.languages.thai') }
  ];

  const LanguageSwitcher = ({ className = '' }) => (
    <div className={`flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-2 text-slate-600 ring-[#1739bd]/30 transition focus-within:border-[#1739bd] focus-within:ring ${className}`}>
      <MdGTranslate className="shrink-0 text-base text-[#1739bd]" aria-hidden="true" />
      <select
        value={currentLanguage}
        onChange={(e) => i18n.changeLanguage(e.target.value)}
        aria-label={t('adminLayout.changeLanguage')}
        className="w-24 bg-transparent text-xs font-semibold text-slate-700 outline-none sm:w-28"
      >
        {languageOptions.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  );

  const navigationItems = [
    { to: '/admin', label: t('dashboard.dashboard'), icon: BsSpeedometer2, end: true },
    { to: '/admin/manage-packages', label: t('dashboard.packages'), icon: BsBoxSeam },
    { to: '/admin/manage-bookings', label: t('dashboard.bookings'), icon: BsJournalBookmark },
    { to: '/admin/users', label: t('dashboard.users'), icon: BsPeople },
    { to: '/admin/video-ad', label: t('dashboard.videoAd'), icon: BsPlayBtn }
  ];

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-900">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(23,57,189,0.18),_transparent_42%),radial-gradient(circle_at_bottom_left,_rgba(23,57,189,0.08),_transparent_36%)]"></div>

      <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 shadow-sm lg:hidden">
        <img src={Logo} alt="Logo" className="h-9 w-auto" />
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-[#1739bd] transition hover:bg-indigo-50"
            onClick={() => setIsMenuOpen((prev) => !prev)}
            aria-label={t('adminLayout.openMenu')}
          >
            {isMenuOpen ? <BsXLg className="text-lg" /> : <BsList className="text-xl" />}
          </button>
        </div>
      </header>

      {isMenuOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 border-0 bg-slate-900/45"
          onClick={() => setIsMenuOpen(false)}
          aria-label={t('adminLayout.closeMenu')}
        />
      )}

      <aside className={`fixed right-3 top-20 z-50 w-[min(22rem,calc(100%-1.5rem))] rounded-3xl border border-slate-200 bg-white p-5 text-slate-900 shadow-2xl backdrop-blur transition-all ${isMenuOpen ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-2 opacity-0'}`}>
        <div>
          <img src={Logo} alt="Logo" className="h-10 w-auto" />
          <span className="mt-2 block text-xs uppercase tracking-[0.2em] text-slate-500">{t('adminLayout.panel')}</span>
        </div>

        <div className="mt-4 self-start">
          <LanguageSwitcher className="px-2.5 py-1.5" />
        </div>

        <Link to="/account" className="mt-4 flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold no-underline" onClick={() => setIsMenuOpen(false)}>
          <BsPersonCircle />
          <span>{currentUser?.fullname || currentUser?.email || t('adminLayout.admin')}</span>
        </Link>

        <div className="mt-4">
          <button type="button" className="flex w-full items-center justify-center gap-2 rounded-2xl bg-rose-500 px-3 py-2 font-semibold text-white transition hover:bg-rose-400" onClick={handleLogout}>
            <BsBoxArrowRight />
            <span>{t('adminLayout.logout')}</span>
          </button>
        </div>
      </aside>

      <div className="relative z-10 flex w-full">
        {/* Desktop Sidebar */}
        <aside className="hidden w-72 shrink-0 rounded-r-3xl border-r border-white/60 bg-white/80 p-4 shadow-[0_18px_60px_rgba(15,23,42,0.09)] backdrop-blur lg:sticky lg:top-0 lg:h-screen lg:flex lg:flex-col">
          <div>
            <img src={Logo} alt="Logo" className="h-11 w-auto rounded-xl" />
            <span className="mt-3 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{t('adminLayout.panel')}</span>
          </div>

          <div className="mt-4 self-start">
            <LanguageSwitcher className="px-2.5 py-1.5" />
          </div>

          {/* Scrollable Navigation Menu */}
          <nav className="mt-4 flex-1 space-y-1.5 overflow-y-auto">
            {navigationItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => `flex items-center gap-3 rounded-2xl px-3 py-2 text-sm font-semibold no-underline transition ${isActive ? 'bg-indigo-50 text-[#1739bd]' : 'text-slate-700 hover:bg-slate-100'}`}
              >
                <item.icon className="text-base" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Fixed User Section at Bottom */}
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-3">
            <Link to="/account" className="flex items-center gap-2 text-sm font-medium text-slate-700 no-underline">
              <BsPersonCircle />
              <span>{currentUser?.fullname || currentUser?.email || t('adminLayout.admin')}</span>
            </Link>
            <button type="button" className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-rose-500 px-3 py-2 text-sm font-semibold text-white transition hover:bg-rose-400" onClick={handleLogout}>
              <BsBoxArrowRight />
              <span>{t('adminLayout.logout')}</span>
            </button>
          </div>
        </aside>

        {/* Main Content Area */}
        <div className="flex min-w-0 flex-1 flex-col gap-5 p-3 lg:p-5">
          <main className="min-h-[calc(100vh-6.5rem)] rounded-3xl border border-white/70 bg-white/85 p-3 shadow-[0_18px_60px_rgba(15,23,42,0.09)] backdrop-blur sm:p-5 lg:p-6">
            <Outlet />
          </main>
        </div>
      </div>

      <nav className="fixed bottom-0 left-0 right-0 z-40 grid h-16 grid-cols-5 border-t border-slate-200 bg-white/95 px-2 backdrop-blur lg:hidden" aria-label={t('adminLayout.navigation')}>
        {navigationItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `inline-flex items-center justify-center rounded-2xl text-xl no-underline transition ${isActive ? 'bg-indigo-50 text-[#1739bd]' : 'text-slate-500'}`}
          >
            <item.icon className="text-lg" />
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export default AdminLayout;